import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Button, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Alert, CircularProgress, Snackbar, InputAdornment, Grid, Divider,
  Select, FormControl, InputLabel, FormHelperText,
} from '@mui/material';
import {
  Add, Delete, ContentCopy, SmartToy, PersonAdd, Visibility, VisibilityOff, Edit, Refresh,
  Info, Article, Search,
} from '@mui/icons-material';
import { useWebSocket } from '../../hooks/useWebSocket';
import apiClient from '../../utils/apiClient';
import { User, Robot } from '../../types';
import { getConfig, addEventListener, removeEventListener } from '../../utils/socketClient';

const RobotRegistry: React.FC = () => {
  const { robotsOnline } = useWebSocket();
  
  // State
  const [robots, setRobots] = useState<Robot[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // v6.3.36: serial-first filter for the registry table.
  const [search, setSearch] = useState('');
  const filteredRobots = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return robots;
    return robots.filter((r: any) =>
      `${r.serial_number || ''} ${r.friendly_name || ''} ${r.model_number || ''} ${r.ip_address || ''}`
        .toLowerCase().includes(q));
  }, [robots, search]);
  
  // Dialog states
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsTarget, setLogsTarget] = useState<{ type: string; id: string; label: string }>({ type: '', id: '', label: '' });
  
  const [selectedRobot, setSelectedRobot] = useState<Robot | null>(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [newRobotSecret, setNewRobotSecret] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [isSaving, setIsSaving] = useState(false);
  
  // Form data for new robot
  const [formData, setFormData] = useState({
    serial_number: '',
    model_number: '',
    firmware_version: '',
    friendly_name: '',
    capabilities_json: '',
  });
  
  // Edit form data - all editable fields
  const [editFormData, setEditFormData] = useState({
    model_number: '',
    mode: '',
    capabilities_json: '',
    metadata_json: '',
  });

  // Fetch functions
  const fetchRobots = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get<Robot[]>('/admin/robots');
      setRobots(response.data);
      setError(null);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to fetch robots');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await apiClient.get<User[]>('/admin/users');
      setUsers(response.data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchRobots();
    fetchUsers();
  }, [fetchRobots, fetchUsers]);

  // When robots list loads and we know which are online, request config sync
  // This ensures the DB mode/config is up-to-date when viewing Registry
  useEffect(() => {
    if (robots.length > 0 && robotsOnline.length > 0) {
      robots.forEach((robot) => {
        if (robotsOnline.includes(robot.serial_number)) {
          // Request fresh config from robot — this triggers robot_config_sync
          // which updates the DB columns (mode, friendly_name, firmware, etc.)
          getConfig(robot.serial_number);
        }
      });
      // Re-fetch robots after a short delay to pick up synced data
      const timer = setTimeout(() => fetchRobots(), 2000);
      return () => clearTimeout(timer);
    }
  }, [robotsOnline.length]); // Only run when online robots list changes

  // Auto-refresh every 60 seconds (rely on WebSocket for real-time updates)
  useEffect(() => {
    const interval = setInterval(fetchRobots, 60000);
    return () => clearInterval(interval);
  }, [fetchRobots]);

  const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleRegister = async () => {
    setIsSaving(true);
    let registrationSuccess = false;
    let secret: string | null = null;
    
    try {
      let capabilities = null;
      if (formData.capabilities_json.trim()) {
        try {
          capabilities = JSON.parse(formData.capabilities_json);
        } catch {
          showSnackbar('Invalid capabilities JSON', 'error');
          setIsSaving(false);
          return;
        }
      }
      
      const response = await apiClient.post('/admin/robots', {
        serial_number: formData.serial_number,
        model_number: formData.model_number,
        firmware_version: formData.firmware_version || null,
        capabilities,
      });
      
      // Registration succeeded
      registrationSuccess = true;
      secret = response.data.robot_secret || null;
      
    } catch (e: any) {
      console.error('Registration error:', e);
      showSnackbar(e.response?.data?.detail || 'Failed to register robot', 'error');
      setIsSaving(false);
      return;
    }
    
    // If registration succeeded, update state and fetch robots
    if (registrationSuccess) {
      if (secret) {
        setNewRobotSecret(secret);
        showSnackbar('Robot registered! Save the secret below.');
      } else {
        showSnackbar('Robot registered successfully!');
        setRegisterDialogOpen(false);
        resetRegisterForm();
      }
      
      // Fetch robots in background (don't let it affect success state)
      try {
        await fetchRobots();
      } catch (e) {
        console.warn('Failed to refresh robots list:', e);
      }
    }
    
    setIsSaving(false);
  };

  const handleEdit = async () => {
    if (!selectedRobot) return;
    setIsSaving(true);
    
    try {
      // Parse JSON fields
      let capabilities = undefined;
      let metadata = undefined;
      
      if (editFormData.capabilities_json.trim()) {
        try {
          capabilities = JSON.parse(editFormData.capabilities_json);
        } catch {
          showSnackbar('Invalid capabilities JSON format', 'error');
          setIsSaving(false);
          return;
        }
      }
      
      if (editFormData.metadata_json.trim()) {
        try {
          metadata = JSON.parse(editFormData.metadata_json);
        } catch {
          showSnackbar('Invalid metadata JSON format', 'error');
          setIsSaving(false);
          return;
        }
      }
      
      const updateData: any = {};
      if (editFormData.model_number) updateData.model_number = editFormData.model_number;
      if (editFormData.mode) updateData.mode = editFormData.mode;
      if (capabilities !== undefined) updateData.capabilities = capabilities;
      if (metadata !== undefined) updateData.metadata = metadata;
      
      await apiClient.patch(`/admin/robots/${selectedRobot.id}`, updateData);
      
      // Sync mode to robot_config.yaml if robot is online
      const isOnline = robotsOnline.includes(selectedRobot.serial_number);
      if (isOnline && editFormData.mode && editFormData.mode !== selectedRobot.mode) {
        const { sendConfig } = await import('../../utils/socketClient');
        sendConfig(selectedRobot.serial_number, {
          robot_id: selectedRobot.id,
          robot_mode: editFormData.mode,
        });
      }
      
      await fetchRobots();
      setEditDialogOpen(false);
      showSnackbar('Robot updated successfully!');
    } catch (e: any) {
      console.error('Update error:', e);
      showSnackbar(e.response?.data?.detail || 'Failed to update robot', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (robotId: string) => {
    if (!window.confirm('Are you sure you want to delete this robot? This action cannot be undone.')) return;
    try {
      await apiClient.delete(`/admin/robots/${robotId}`);
      await fetchRobots();
      showSnackbar('Robot deleted');
    } catch (e: any) {
      showSnackbar(e.response?.data?.detail || 'Failed to delete robot', 'error');
    }
  };

  const handleAssign = async () => {
    if (!selectedRobot) return;
    setIsSaving(true);
    try {
      await apiClient.post(`/admin/robots/${selectedRobot.id}/assign`, {
        user_id: selectedUserId || null,
      });
      await fetchRobots();
      setAssignDialogOpen(false);
      setSelectedRobot(null);
      setSelectedUserId('');
      showSnackbar('Robot assigned successfully!');
    } catch (e: any) {
      showSnackbar(e.response?.data?.detail || 'Failed to assign robot', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      // Modern async clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        showSnackbar('Copied to clipboard!');
        return;
      }
    } catch (err) {
      console.warn('Clipboard API failed, trying fallback:', err);
    }
    
    // Fallback for older browsers or HTTP contexts
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      if (successful) {
        showSnackbar('Copied to clipboard!');
      } else {
        showSnackbar('Failed to copy - please copy manually', 'error');
      }
    } catch (err) {
      console.error('Fallback copy failed:', err);
      showSnackbar('Failed to copy - please copy manually', 'error');
    }
  };

  const openEditDialog = (robot: Robot) => {
    setSelectedRobot(robot);
    const robotMeta = robot.robot_metadata || robot.metadata;
    setEditFormData({
      model_number: robot.model_number || '',
      mode: robot.mode || 'setup',
      capabilities_json: robot.capabilities ? JSON.stringify(robot.capabilities, null, 2) : '',
      metadata_json: robotMeta ? JSON.stringify(robotMeta, null, 2) : '',
    });
    setEditDialogOpen(true);
    
    // If robot is online, fetch latest config
    if (robotsOnline.includes(robot.serial_number)) {
      const handleRobotConfig = (data: any) => {
        const matchesRobot = !data.robot_serial || data.robot_serial === robot.serial_number;
        if (matchesRobot && data.config) {
          if (data.config.robot_mode) {
            setEditFormData(prev => ({
              ...prev,
              mode: data.config.robot_mode,
            }));
          }
        }
        removeEventListener('robot_config', handleRobotConfig);
      };
      
      addEventListener('robot_config', handleRobotConfig);
      getConfig(robot.serial_number);
      
      setTimeout(() => {
        removeEventListener('robot_config', handleRobotConfig);
      }, 5000);
    }
  };

  const openAssignDialog = (robot: Robot) => {
    setSelectedRobot(robot);
    setSelectedUserId(robot.owner_id || '');
    setAssignDialogOpen(true);
  };

  const openDetailDialog = (robot: Robot) => {
    setSelectedRobot(robot);
    setDetailDialogOpen(true);
  };

  const openLogsDialog = async (targetType: string, targetId: string, label: string) => {
    setLogsTarget({ type: targetType, id: targetId, label });
    setLogsDialogOpen(true);
    setLogsLoading(true);
    try {
      const response = await apiClient.get('/admin/audit-logs', {
        params: { target_type: targetType, target_id: targetId, limit: 50 }
      });
      setAuditLogs(response.data.logs || []);
    } catch {
      setAuditLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const resetRegisterForm = () => {
    setFormData({ serial_number: '', model_number: '', firmware_version: '', friendly_name: '', capabilities_json: '' });
    setNewRobotSecret(null);
    setShowSecret(false);
  };

  const getOwnerEmail = (ownerId: string | null | undefined): string => {
    if (!ownerId) return 'Unassigned';
    const owner = users.find((u) => u.id === ownerId);
    return owner?.email || 'Unknown';
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>Robot Registry</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<Refresh />} onClick={fetchRobots} disabled={isLoading}>Refresh</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => { resetRegisterForm(); setRegisterDialogOpen(true); }}>
            Register Robot
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <TextField
        size="small" fullWidth placeholder="Filter by serial number, name, model or IP…"
        value={search} onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, maxWidth: 460 }}
        InputProps={{ startAdornment: (
          <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
        ) }}
      />

      {/* Robots Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Robot</TableCell>
                    <TableCell>ID</TableCell>
                    <TableCell>Model / Firmware</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Mode</TableCell>
                    <TableCell>IP Address</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRobots.length === 0 && (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {robots.length === 0 ? 'No robots registered.' : `No robots match "${search}".`}
                      </Typography>
                    </TableCell></TableRow>
                  )}
                  {filteredRobots.map((robot) => {
                    const isOnline = robotsOnline.includes(robot.serial_number);
                    const robotMeta = robot.robot_metadata || {};
                    const lastIp = robotMeta.last_ip || '-';
                    return (
                      <TableRow key={robot.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SmartToy sx={{ color: isOnline ? 'success.main' : 'text.secondary' }} />
                            <Box>
                              {/* v6.3.36: serial leads — it's the unique key */}
                              <Typography variant="body2" fontWeight={600}>
                                {robot.serial_number}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {robot.friendly_name || '—'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                            {robot.id?.slice(0, 8)}...
                          </Typography>
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); copyToClipboard(robot.id); }}>
                            <ContentCopy sx={{ fontSize: 14 }} />
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{robot.model_number}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            v{robot.firmware_version || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={isOnline ? 'Online' : robot.status || 'offline'}
                            sx={{
                              backgroundColor: isOnline ? 'rgba(105, 240, 174, 0.2)' : 'rgba(255, 82, 82, 0.2)',
                              color: isOnline ? '#69f0ae' : '#ff5252',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={robot.mode || 'setup'}
                            sx={{
                              backgroundColor: robot.mode === 'operational' ? 'rgba(33, 150, 243, 0.2)' 
                                : robot.mode === 'active' ? 'rgba(105, 240, 174, 0.2)'
                                : robot.mode === 'maintenance' ? 'rgba(255, 152, 0, 0.2)' 
                                : robot.mode === 'expired' ? 'rgba(255, 82, 82, 0.2)'
                                : 'rgba(158, 158, 158, 0.2)',
                              color: robot.mode === 'operational' ? '#2196f3' 
                                : robot.mode === 'active' ? '#69f0ae'
                                : robot.mode === 'maintenance' ? '#ff9800' 
                                : robot.mode === 'expired' ? '#ff5252'
                                : '#9e9e9e',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {lastIp}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color={robot.owner_id ? 'text.primary' : 'text.secondary'}>
                            {getOwnerEmail(robot.owner_id)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {robot.created_at ? new Date(robot.created_at).toLocaleDateString() : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openDetailDialog(robot)} title="Details">
                            <Info fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => openEditDialog(robot)} title="Edit">
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => openLogsDialog('robot', robot.id, robot.serial_number)} title="View Logs">
                            <Article fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => openAssignDialog(robot)} title="Assign">
                            <PersonAdd fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(robot.id)} title="Delete">
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {robots.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No robots registered yet</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Register Robot Dialog */}
      <Dialog open={registerDialogOpen} onClose={() => setRegisterDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{newRobotSecret ? '✓ Robot Registered!' : 'Register New Robot'}</DialogTitle>
        <DialogContent>
          {newRobotSecret ? (
            <Box sx={{ pt: 1 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Robot registered and assigned to you! Save the secret below - it won't be shown again!
              </Alert>
              <TextField
                label="Robot Secret"
                value={newRobotSecret}
                fullWidth
                InputProps={{
                  readOnly: true,
                  type: showSecret ? 'text' : 'password',
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowSecret(!showSecret)}>
                        {showSecret ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                      <IconButton onClick={() => copyToClipboard(newRobotSecret)}>
                        <ContentCopy />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          ) : (
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Serial Number"
                value={formData.serial_number}
                onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                fullWidth
                required
                helperText="Unique identifier for the robot (e.g., ROBOT_001)"
              />
              <TextField
                label="Model Number"
                value={formData.model_number}
                onChange={(e) => setFormData({ ...formData, model_number: e.target.value })}
                fullWidth
                required
                helperText="e.g., AMR_V1, TurtleBot3"
              />
              <TextField
                label="Firmware Version"
                value={formData.firmware_version}
                onChange={(e) => setFormData({ ...formData, firmware_version: e.target.value })}
                fullWidth
                helperText="e.g., 1.0.0"
              />
              <TextField
                label="Capabilities (JSON)"
                value={formData.capabilities_json}
                onChange={(e) => setFormData({ ...formData, capabilities_json: e.target.value })}
                fullWidth
                multiline
                rows={3}
                helperText='e.g., {"has_arm": true, "camera_type": "depth"}'
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {newRobotSecret ? (
            <Button variant="contained" onClick={() => setRegisterDialogOpen(false)}>Done</Button>
          ) : (
            <>
              <Button onClick={() => setRegisterDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleRegister}
                disabled={!formData.serial_number || !formData.model_number || isSaving}
              >
                {isSaving ? 'Registering...' : 'Register'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Robot Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Robot: {selectedRobot?.serial_number}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Robot ID: <strong>{selectedRobot?.id}</strong>
                  <IconButton size="small" onClick={() => selectedRobot && copyToClipboard(selectedRobot.id)}>
                    <ContentCopy sx={{ fontSize: 14 }} />
                  </IconButton>
                </Alert>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Serial Number"
                  value={selectedRobot?.serial_number || ''}
                  fullWidth
                  disabled
                  helperText="Serial number cannot be changed"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Model Number"
                  value={editFormData.model_number}
                  onChange={(e) => setEditFormData({ ...editFormData, model_number: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Mode</InputLabel>
                  <Select
                    value={editFormData.mode}
                    label="Mode"
                    onChange={(e) => setEditFormData({ ...editFormData, mode: e.target.value })}
                  >
                    <MenuItem value="setup">Setup</MenuItem>
                    <MenuItem value="maintenance">Maintenance</MenuItem>
                    <MenuItem value="operational">Operational</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="expired">Expired</MenuItem>
                  </Select>
                  <FormHelperText>Robot operation mode</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Owner"
                  value={getOwnerEmail(selectedRobot?.owner_id)}
                  fullWidth
                  disabled
                  helperText="Use 'Assign' button to change owner"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Capabilities (JSON)"
                  value={editFormData.capabilities_json}
                  onChange={(e) => setEditFormData({ ...editFormData, capabilities_json: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                  helperText='e.g., {"has_arm": true, "camera_type": "depth"}'
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Metadata (JSON)"
                  value={editFormData.metadata_json}
                  onChange={(e) => setEditFormData({ ...editFormData, metadata_json: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                  helperText="Additional metadata (auto-charge settings, etc.)"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Robot Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Robot to User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Assign <strong>{selectedRobot?.serial_number}</strong> to a user:
            </Typography>
            <TextField
              label="Select User"
              select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              fullWidth
            >
              <MenuItem value="">
                <em>Unassigned</em>
              </MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.email} ({user.role})
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAssign} disabled={isSaving}>
            {isSaving ? 'Assigning...' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Robot Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Robot Details</DialogTitle>
        <DialogContent>
          {selectedRobot && (
            <Box sx={{ pt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>Identification</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Robot ID</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {selectedRobot.id}
                    </Typography>
                    <IconButton size="small" onClick={() => copyToClipboard(selectedRobot.id)}>
                      <ContentCopy sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Serial Number</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">{selectedRobot.serial_number}</Typography>
                    <IconButton size="small" onClick={() => copyToClipboard(selectedRobot.serial_number)}>
                      <ContentCopy sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                </Grid>
                
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>Hardware Info</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Model Number</Typography>
                  <Typography variant="body2">{selectedRobot.model_number}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Firmware Version</Typography>
                  <Typography variant="body2">{selectedRobot.firmware_version || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Chip
                    size="small"
                    label={robotsOnline.includes(selectedRobot.serial_number) ? 'Online' : selectedRobot.status}
                    sx={{
                      backgroundColor: robotsOnline.includes(selectedRobot.serial_number) 
                        ? 'rgba(105, 240, 174, 0.2)' 
                        : 'rgba(255, 82, 82, 0.2)',
                      color: robotsOnline.includes(selectedRobot.serial_number) ? '#69f0ae' : '#ff5252',
                    }}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Mode</Typography>
                  <Chip
                    size="small"
                    label={selectedRobot.mode || 'setup'}
                    sx={{
                      backgroundColor: selectedRobot.mode === 'operational' ? 'rgba(33, 150, 243, 0.2)' 
                        : selectedRobot.mode === 'active' ? 'rgba(105, 240, 174, 0.2)'
                        : selectedRobot.mode === 'maintenance' ? 'rgba(255, 152, 0, 0.2)' 
                        : selectedRobot.mode === 'expired' ? 'rgba(255, 82, 82, 0.2)'
                        : 'rgba(158, 158, 158, 0.2)',
                      color: selectedRobot.mode === 'operational' ? '#2196f3' 
                        : selectedRobot.mode === 'active' ? '#69f0ae'
                        : selectedRobot.mode === 'maintenance' ? '#ff9800' 
                        : selectedRobot.mode === 'expired' ? '#ff5252'
                        : '#9e9e9e',
                    }}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Friendly Name</Typography>
                  <Typography variant="body2">{selectedRobot.friendly_name || 'N/A'}</Typography>
                </Grid>
                
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>Ownership</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Owner ID</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {selectedRobot.owner_id || 'Unassigned'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Owner Email</Typography>
                  <Typography variant="body2">{getOwnerEmail(selectedRobot.owner_id)}</Typography>
                </Grid>
                
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>Timestamps</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Created At</Typography>
                  <Typography variant="body2">
                    {selectedRobot.created_at ? new Date(selectedRobot.created_at).toLocaleString() : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Last Seen</Typography>
                  <Typography variant="body2">
                    {selectedRobot.last_seen ? new Date(selectedRobot.last_seen).toLocaleString() : 'Never'}
                  </Typography>
                </Grid>
                
                {selectedRobot.capabilities && typeof selectedRobot.capabilities === "object" && Object.keys(selectedRobot.capabilities).length > 0 && (
                  <>
                    <Grid item xs={12}><Divider /></Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="primary" gutterBottom>Capabilities</Typography>
                      <Box sx={{ bgcolor: 'rgba(0,0,0,0.2)', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                        {JSON.stringify(selectedRobot.capabilities, null, 2)}
                      </Box>
                    </Grid>
                  </>
                )}
                
                {((selectedRobot.robot_metadata && typeof selectedRobot.robot_metadata === "object" && Object.keys(selectedRobot.robot_metadata).length > 0) || 
                  (selectedRobot.metadata && typeof selectedRobot.metadata === "object" && Object.keys(selectedRobot.metadata).length > 0)) && (
                  <>
                    <Grid item xs={12}><Divider /></Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="primary" gutterBottom>Metadata</Typography>
                      <Box sx={{ bgcolor: 'rgba(0,0,0,0.2)', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                        {JSON.stringify(selectedRobot.robot_metadata || selectedRobot.metadata, null, 2)}
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
          <Button variant="outlined" onClick={() => { setDetailDialogOpen(false); openEditDialog(selectedRobot!); }}>
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Audit Logs Dialog */}
      <Dialog open={logsDialogOpen} onClose={() => setLogsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Audit Logs — {logsTarget.label}</DialogTitle>
        <DialogContent>
          {logsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : auditLogs.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No logs found</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="caption">{log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={log.action} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', wordBreak: 'break-all' }}>
                          {log.details ? JSON.stringify(log.details) : '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setLogsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default RobotRegistry;
